import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  Grid,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SchoolIcon        from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AssessmentIcon    from '@mui/icons-material/Assessment';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import ArticleIcon       from '@mui/icons-material/Article';

import LICENSE_TEXT from '../constants/licenseText';

function WelcomeScreen({ onComplete }) {
  const [licenseOpen, setLicenseOpen] = useState(false);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            background: 'white'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '-0.5px' }}
            >
              CRE — Casualty Rate Estimator
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Medical Planning Tool
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.5, letterSpacing: 2, textTransform: 'uppercase' }}
            >
              Brigade · Division · Corps
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Single disclaimer — one is enough */}
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              EDUCATIONAL USE ONLY
            </Typography>
            <Typography variant="body2">
              Developed by <strong>Tatar Radu MD</strong> for military medical
              planning education. Not validated for operational use without
              authorisation from qualified military medical authorities.
            </Typography>
          </Alert>

          {/* Three capability columns */}
          <Grid container spacing={2} sx={{ mb: 4, textAlign: 'center' }}>
            <Grid item xs={4}>
              <SchoolIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="caption" display="block" color="text.secondary">
                Casualty<br />Planning
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <LocalHospitalIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="caption" display="block" color="text.secondary">
                Medical<br />Logistics
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <AssessmentIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
              <Typography variant="caption" display="block" color="text.secondary">
                Exercise<br />Support
              </Typography>
            </Grid>
          </Grid>

          {/* Action button */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              onClick={onComplete}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 3
              }}
            >
              Begin Planning →
            </Button>
          </Box>

          {/* Footer — copyright + licence link */}
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', textAlign: 'center' }}
          >
            © 2025–2026 Tatar Radu, MD — Proprietary Software · v3.0 · March 2026 ·{' '}
            <Link
              component="button"
              variant="caption"
              color="text.disabled"
              underline="always"
              onClick={() => setLicenseOpen(true)}
              sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
            >
              Licence
            </Link>
          </Typography>
        </Paper>
      </Container>

      {/* ── Licence dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={licenseOpen}
        onClose={() => setLicenseOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="action" fontSize="small" />
          Software Licence — PSP/CRE Tool
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'text.primary',
              m: 0,
            }}
          >
            {LICENSE_TEXT}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLicenseOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WelcomeScreen;
